import { cn } from '@invent/wl-ui-kit-next';
import {
  DropdownMenuItem,
  DropdownMenuShortcut
} from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import { AutosaveMenuItem, DiscardChangesDialog, SaveMenuButton, useReaderSetting } from '@/design';
import { formatAgo } from './template-fields';

/**
 * An email template's and the design's saving, the Lists way (SR-412, the saving kit in `@/design`).
 * The live version is shared by the whole team and changes only by **Publish…**; the draft is the
 * reader's working copy, so it saves itself — the analogue of a private view on Lists.
 *
 * - **One `Save ▾`**: *Publish…*, *Save draft* (Ctrl+S), *Discard changes* (back to the saved
 *   draft), *Discard draft…* (confirmed, back to the live version), then the reader's *Autosave
 *   drafts* — on until they turn it off, one setting for every template and the design.
 * - **Its edge and dot** while the editor holds what is not live: "Changed — not saved" while edits
 *   are not in the draft, "Draft — not published" once they are. Beside it "Saving…" / "Draft
 *   saved", or "Not saved" with the refusal on hover.
 * - **Autosave** goes `AUTOSAVE_MS` after the last edit, one save at a time, only while the editor
 *   says it is `savable` (no problems, no placeholder list open); an edit already sent or refused is
 *   not sent again. A 409 stops it until *Reload*.
 * - **Leaving** with edits not in the draft — another template or variant, the overview, a link
 *   out, an action that reads the editor afresh — saves first where autosave may, and goes on once
 *   it lands; otherwise the kit's "Discard N unsaved changes?". The tab's close or reload asks the
 *   browser's own question. Publish… waits for a save already out.
 */

export const AUTOSAVE_MS = 2000;

/** A draft save's outcome: kept, refused by a revision conflict, or refused otherwise. */
export type DraftAnswer = 'landed' | 'conflict' | 'refused';

const BUSY_WORDS: Partial<Record<string, string>> = {
  publish: 'Publishing…',
  discard: 'Discarding…',
  withdraw: 'Withdrawing…',
  remove: 'Removing…'
};

export interface IDraftSavingArgs {
  /** Edits the draft does not hold, as rendered. */
  changed: boolean;
  /** The same, as of now — read after a save lands, before the screen has drawn it. */
  isChanged: () => boolean;
  /** A rich part's edit typed and not yet compiled — not in `changed` until it is. */
  pending: () => boolean;
  /** Every rich part's last edit compiled and handed out: leaving reads `isChanged` after it. */
  flushEdits: () => Promise<unknown>;
  hasDraft: boolean;
  /** When the draft was last saved, said on hover. */
  draftAt: Nullable<string>;
  /** No problems in the parts and no placeholder list open: autosave may go. */
  savable: boolean;
  /** What leaving needs to save first: nothing the form itself refuses and no placeholder list
   *  open — survey-api judges the rest, and its refusal asks. */
  flushable: boolean;
  canSave: boolean;
  canPublish: boolean;
  /** The editor's write out, if one is — `draft` for a save. */
  busy: Nullable<string>;
  /** Bumped on every edit: an edit already sent or refused is not sent again. */
  edits: number;
  /** The parts changed, for "Discard N unsaved changes?". */
  count: () => number;
  /** The last refusal in words — "Not saved"'s hover. */
  refusal: Nullable<string>;
  save: () => Promise<DraftAnswer>;
  onPublish: () => void;
  onDiscardChanges: () => void;
  onDiscardDraft: () => void;
  /** What *Discard draft…* confirms. */
  discardDraftWords: string;
}

export function useDraftSaving(args: IDraftSavingArgs) {
  const { changed, hasDraft, savable, busy, edits } = args;
  const [autosaveOn, setAutosaveOn] = useReaderSetting('veritant.mail.autosave', true);
  const [stopped, setStopped] = React.useState(false);
  const [refusedAt, setRefusedAt] = React.useState<Nullable<number>>(null);
  const [asking, setAsking] = React.useState<Nullable<{ count: number; go: () => void }>>(null);
  const [confirming, setConfirming] = React.useState(false);
  const latest = React.useRef(args);
  latest.current = args;
  const editsNow = React.useRef(edits);
  editsNow.current = edits;
  const on = React.useRef(autosaveOn && !stopped);
  on.current = autosaveOn && !stopped;
  const sentAt = React.useRef<Nullable<number>>(null);
  const inFlight = React.useRef<Nullable<Promise<DraftAnswer>>>(null);
  // The editor drops itself (Discard, a publication read afresh): nothing is saved as it goes.
  const quiet = React.useRef(false);
  // A link let through after its save landed or its changes were discarded.
  const passing = React.useRef<Nullable<Element>>(null);
  // What a dialog's choice runs once the dialog has gone: run under it, a move that unmounts the
  // editor would take the dialog with it mid-close and leave the page unclickable.
  const afterClose = React.useRef<Nullable<() => void>>(null);
  const closed = () => {
    const next = afterClose.current;
    afterClose.current = null;
    next?.();
  };

  const run = (): Promise<DraftAnswer> => {
    const at = editsNow.current;
    const going = latest.current
      .save()
      .then((answer) => {
        if (answer === 'landed') {
          sentAt.current = at;
          setRefusedAt(null);
        } else if (answer === 'conflict') {
          setStopped(true);
        } else {
          setRefusedAt(at);
        }
        return answer;
      })
      .finally(() => {
        inFlight.current = null;
      });
    inFlight.current = going;
    return going;
  };

  /** The edits saved now, after the save out — true once nothing is owed. */
  const flush = async (): Promise<boolean> => {
    if (inFlight.current) await inFlight.current;
    if (!latest.current.isChanged()) return true;
    if (!latest.current.flushable || !on.current) return false;
    return (await run()) === 'landed';
  };

  /** Runs `go` once nothing is owed: saved first where autosave may, else asked. */
  const leave = (go: () => void) => {
    void (async () => {
      await latest.current.flushEdits();
      if (!latest.current.isChanged()) return go();
      if (on.current && (await flush())) return go();
      setAsking({ count: latest.current.count(), go });
    })();
  };
  const leaveNow = React.useRef(leave);
  leaveNow.current = leave;

  // A moment after the last edit, while the reader's autosave is on.
  React.useEffect(() => {
    if (!autosaveOn || stopped || !changed || !savable || busy !== null) return;
    if (edits === refusedAt || edits === sentAt.current) return;
    const timer = window.setTimeout(() => {
      if (inFlight.current === null) void run();
    }, AUTOSAVE_MS);
    return () => window.clearTimeout(timer);
    // `run` reads the latest through refs.
  }, [autosaveOn, stopped, changed, savable, busy, edits, refusedAt]);

  // Ctrl+S / Cmd+S saves the draft whenever *Save draft* would.
  React.useEffect(() => {
    const keydown = (event: KeyboardEvent) => {
      if (!(event.ctrlKey || event.metaKey) || event.altKey || event.key.toLowerCase() !== 's') return;
      event.preventDefault();
      if (latest.current.canSave) void runNow.current();
    };
    window.addEventListener('keydown', keydown);
    return () => window.removeEventListener('keydown', keydown);
  }, []);
  const runNow = React.useRef(run);
  runNow.current = run;

  // The tab's close or reload asks while edits are not in the draft — a body's edit still being
  // compiled among them; a link out leaves through `leave`. The app's router is not a data router
  // (no `useBlocker`): a link is caught on its way down, before the router's own handler, and
  // pressed again once it may go.
  React.useEffect(() => {
    const owed = () => latest.current.isChanged() || latest.current.pending();
    const unload = (event: BeforeUnloadEvent) => {
      if (!owed()) return;
      event.preventDefault();
      event.returnValue = '';
    };
    const click = (event: MouseEvent) => {
      const link = (event.target as Nullable<Element>)?.closest?.('a[href]');
      if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (link.getAttribute('target') === '_blank' || link.closest('[contenteditable="true"]')) return;
      if (passing.current === link) {
        passing.current = null;
        return;
      }
      if (!owed()) return;
      event.preventDefault();
      event.stopPropagation();
      leaveNow.current(() => {
        passing.current = link;
        (link as HTMLElement).click();
        passing.current = null;
      });
    };
    window.addEventListener('beforeunload', unload);
    document.addEventListener('click', click, true);
    return () => {
      window.removeEventListener('beforeunload', unload);
      document.removeEventListener('click', click, true);
    };
  }, []);

  // An edit after a discard that did not go through: the editor is kept after all.
  React.useEffect(() => {
    quiet.current = false;
  }, [edits]);

  // Left another way — the browser's Back, a reload of the template — edits owed are saved.
  React.useEffect(
    () => () => {
      if (!quiet.current && on.current && latest.current.isChanged() && latest.current.flushable) void flush();
    },
    []
  );

  const healthy = autosaveOn && !stopped && savable && edits !== refusedAt;
  const failed = changed && refusedAt === edits;
  const mark =
    busy === 'draft' || (changed && healthy && busy === null)
      ? { state: 'saving', text: 'Saving…' }
      : failed
        ? { state: 'failed', text: 'Not saved' }
        : !changed && hasDraft
          ? { state: 'saved', text: 'Draft saved' }
          : null;
  const now = Date.now();
  const publishReason = args.canPublish
    ? null
    : !changed && !hasDraft
      ? 'Nothing changed from the live version'
      : !savable
        ? 'Fix the problems marked first'
        : null;

  const controls = (
    <span className="flex items-center gap-2">
      {mark && (
        <span
          role="status"
          data-draft-state={mark.state}
          data-draft-saved={mark.state === 'saved' ? '' : undefined}
          title={
            mark.state === 'failed'
              ? (args.refusal ?? undefined)
              : mark.state === 'saved' && args.draftAt
                ? `Saved ${formatAgo(args.draftAt, now)}`
                : undefined
          }
          className={cn('text-xs whitespace-nowrap', mark.state === 'failed' ? 'text-destructive' : 'text-muted-foreground')}
        >
          {mark.text}
        </span>
      )}
      <SaveMenuButton
        label="Save"
        unsaved={changed || hasDraft}
        unsavedWords={changed ? 'Changed — not saved' : 'Draft — not published'}
        busy={busy !== null && busy !== 'draft'}
        busyLabel={busy ? BUSY_WORDS[busy] : undefined}
        align="end"
        menuClassName="w-72"
        footer={
          <AutosaveMenuItem
            checked={autosaveOn}
            onCheckedChange={setAutosaveOn}
            label="Autosave drafts"
            hint="Your setting — publishing is always by hand"
          />
        }
      >
        <DropdownMenuItem disabled={!args.canPublish} onSelect={args.onPublish}>
          <span className="grid">
            Publish…
            {publishReason && <span className="text-xs text-muted-foreground">{publishReason}</span>}
          </span>
        </DropdownMenuItem>
        <DropdownMenuItem disabled={!args.canSave} onSelect={() => void run()}>
          <span className="grid">
            Save draft
            {!changed && (
              <span className="text-xs text-muted-foreground">
                {hasDraft ? 'Everything is in the draft' : 'Nothing changed'}
              </span>
            )}
          </span>
          <DropdownMenuShortcut>Ctrl+S</DropdownMenuShortcut>
        </DropdownMenuItem>
        <DropdownMenuItem disabled={!changed} onSelect={args.onDiscardChanges}>
          <span className="grid">
            Discard changes
            {changed && (
              <span className="text-xs text-muted-foreground">
                {hasDraft ? 'Back to the saved draft' : 'Back to the live version'}
              </span>
            )}
          </span>
        </DropdownMenuItem>
        <DropdownMenuItem disabled={!hasDraft || busy !== null} onSelect={() => setConfirming(true)}>
          Discard draft…
        </DropdownMenuItem>
      </SaveMenuButton>
    </span>
  );

  const dialogs = (
    <>
      <DiscardChangesDialog
        open={asking !== null}
        count={asking?.count ?? 0}
        description="They are not in the draft — Save draft keeps them. Leaving drops them."
        onCancel={() => setAsking(null)}
        onDiscard={() => {
          const go = asking?.go;
          setAsking(null);
          quiet.current = true;
          afterClose.current = go ?? null;
        }}
        onClosed={closed}
      />
      <DiscardChangesDialog
        open={confirming}
        title="Discard the draft?"
        action="Discard the draft"
        description={args.discardDraftWords}
        onCancel={() => setConfirming(false)}
        onDiscard={() => {
          quiet.current = true;
          afterClose.current = args.onDiscardDraft;
        }}
        onClosed={closed}
      />
    </>
  );

  return {
    controls,
    dialogs,
    leave,
    /** Edits on screen that autosave is not about to keep — `*` before the name. */
    unsaved: changed && !healthy,
    /** After *Reload*: autosave goes again. */
    resume: () => {
      setStopped(false);
      setRefusedAt(null);
    },
    /** The editor is about to be read afresh: nothing is saved as it goes. */
    quiet: () => {
      quiet.current = true;
    },
    /** The save out, if one is, landed or refused — before a write that quotes the draft's revision. */
    settle: async () => {
      if (inFlight.current) await inFlight.current;
    }
  };
}
